// pages/admin/BlogList.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import {
    deleteBlogAPI,
    getAllBlogsAPI,
    toggleHideBlogAPI,
    togglePinBlogAPI,
} from "../../../../api/blogAPIs.ts";
import {
    BlockBetween,
    BlockContent,
    BlockDes,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    Icon,
    PreviewCard,
    ReactDataTable,
} from "../../../../components/Component";
import DeleteModal from "../../../../components/DeleteModal";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const BlogList = () => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState({});

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                setLoading(true);
                const response = await getAllBlogsAPI();
                setBlogs(response.data.data);
            } catch (error) {
                console.error("Failed to fetch blogs:", error);
                toast.error("Failed to fetch blogs");
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, []);

    const toggleDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen);

    const handleDelete = async (blog) => {
        try {
            await deleteBlogAPI({ id: blog._id });
            setBlogs((prev) => prev.filter((b) => b._id !== blog._id));
            toast.success("Blog deleted successfully");
        } catch (error) {
            console.error("Failed to delete blog:", error);
            toast.error("Failed to delete blog");
        }
    };

    const toggleStatusDropdown = (blogId) => {
        setStatusDropdownOpen((prev) => ({
            ...prev,
            [blogId]: !prev[blogId],
        }));
    };

    const handleTogglePin = async (blogId) => {
        try {
            const response = await togglePinBlogAPI({ id: blogId });
            setBlogs((prev) =>
                prev.map((blog) =>
                    blog._id === blogId ? { ...blog, isPinned: response.data.data.isPinned } : blog
                )
            );
            toast.success(`Blog ${response.data.data.isPinned ? "pinned" : "unpinned"} successfully`);
        } catch (error) {
            console.error("Failed to toggle pin:", error);
            toast.error("Failed to toggle pin");
        }
    };

    const handleToggleHide = async (blogId) => {
        try {
            const response = await toggleHideBlogAPI({ id: blogId });
            setBlogs((prev) =>
                prev.map((blog) =>
                    blog._id === blogId ? { ...blog, isHidden: response.data.data.isHidden } : blog
                )
            );
            toast.success(`Blog ${response.data.data.isHidden ? "hidden" : "unhidden"} successfully`);
        } catch (error) {
            console.error("Failed to toggle hide:", error);
            toast.error("Failed to toggle hide");
        }
    };

    const columns = [
        {
            name: "Title",
            selector: (row) => row.title,
            cell: (row) => (
                <div className="d-flex align-center m-1 g-2" style={{ cursor: "pointer" }}>
                    <img
                        src={row.imageUrl || "https://via.placeholder.com/50"}
                        alt={row.title}
                        style={{ width: "50px", height: "50px", borderRadius: "5%", objectFit: "cover" }}
                    />
                    <div
                        className="fw-bold cursor-pointer"
                        onClick={() => navigate(`/admin/blogs/${row._id}`)}
                    >
                        {row.title}
                    </div>
                </div>
            ),
        },
        {
            name: "Sequence",
            selector: (row) => row.sequence,
            cell: (row) => <span>{row.sequence}</span>,
            sortable: true,
        },
        {
            name: "Status",
            selector: (row) => row.isPinned,
            cell: (row) => (
                <Dropdown
                    isOpen={statusDropdownOpen[row._id] || false}
                    toggle={() => toggleStatusDropdown(row._id)}
                >
                    <DropdownToggle
                        caret
                        color={row.isPinned ? "success" : "secondary"}
                        className="btn-sm"
                    >
                        {row.isPinned ? "Pinned" : "Not Pinned"}
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem onClick={() => handleTogglePin(row._id)}>
                            {row.isPinned ? "Unpin" : "Pin"}
                        </DropdownItem>
                        <DropdownItem onClick={() => handleToggleHide(row._id)}>
                            {row.isHidden ? "Unhide" : "Hide"}
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            ),
            sortable: true,
        },
        {
            name: "Views",
            selector: (row) => row.views,
            cell: (row) => <span>{row.views}</span>,
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <Button
                        color="primary"
                        size="sm"
                        onClick={() => navigate(`/admin/blogs/edit/${row._id}`)}
                    >
                        <Icon name="edit" /> Edit
                    </Button>
                    <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                            setSelectedBlog(row);
                            toggleDeleteModal();
                        }}
                    >
                        <Icon name="trash" /> Delete
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Head title="Blog List" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Blog List
                            </BlockTitle>
                            <BlockDes className="text-soft">
                                <p>You have a total of {blogs.length} blogs.</p>
                            </BlockDes>
                        </BlockHeadContent>
                        <BlockHeadContent>
                            <Button color="success" onClick={() => navigate("/admin/blogs/add")}>
                                Add Blog
                            </Button>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>
                <BlockContent>
                    <PreviewCard>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                                <div className="text-center">
                                    <div className="spinner-grow text-primary" style={{ width: "3rem", height: "3rem" }} role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading blogs, please wait...</p>
                                </div>
                            </div>
                        ) : (
                            <ReactDataTable
                                data={blogs}
                                columns={columns}
                                pagination
                                actions
                                expandableRows
                                className=""
                            />
                        )}
                    </PreviewCard>
                </BlockContent>
            </Content>
            <DeleteModal
                isOpen={isDeleteModalOpen}
                toggle={toggleDeleteModal}
                onConfirm={handleDelete}
                item={selectedBlog}
                itemName="blog"
            />
        </React.Fragment>
    );
};

export default BlogList;